import { Column, Entity, Index, JoinColumn, JoinTable, ManyToMany, ManyToOne, OneToMany, PrimaryGeneratedColumn } from 'typeorm';
import { Business, PublicBusiness } from '../business/business.entity';
import { Project, PublicProject } from '../project/project.entity';
import { PublicSite, Site } from '../site/site.entity';
import { PublicUser, User } from '../user/user.entity';
import { BusinessUser } from '../business/business-user.entity';
import { AssetCollection } from '../asset-collection/asset-collection.entity';

export type PublicAsset = Pick<
	Asset,
	| 'id'
	| 'type'
	| 'provider'
	| 'title'
	| 'description'
	| 'siteId'
	| 'businessId'
	| 'projectId'
	| 'userId'
	| 'assetPath'
	| 'created'
	| 'dimensions'
	| 'mimeType'
	| 'metadata'
	| 'isRemote'
	| 'localPath'
	| 'collectionIds'
	| 'delete'
> & {
	site?: PublicSite;
	business?: PublicBusiness;
	project?: PublicProject;
	user?: PublicUser;
	isReadOnly?: boolean;
	collections?: Partial<AssetCollection>[];
};

export enum Type {
	Image = 'image',
	Music = 'music',
	Voice = 'voice',
	Video = 'video',
	Audio = 'audio',
	Font = 'font',
	Document = 'document'
}

export enum Provider {
	Amazon = 'amazon',
	Cloudinary = 'cloudinary',
	External = 'external'
}

/**
 * Discriminator for chat-scoped uploads, written to `metadata.chat.kind`.
 * Auto-generated artifacts (e.g. editor screenshots captured by the agent)
 * are tagged so they can be excluded from the user's uploads/library
 * listings and given a short cleanup TTL. User-dropped chat attachments
 * carry no `kind` and are treated as ordinary uploads.
 */
export enum AttachmentKind {
	EditorScreenshot = 'editor-screenshot'
}

export interface AssetDimensions {
	width: number;
	height: number;
}

@Entity('assets')
export class Asset {
	constructor(value?: Partial<Asset>) {
		for (const k in value) {
			this[k] = value[k];
		}
	}

	@PrimaryGeneratedColumn('uuid')
	public id: string;

	@Column({
		type: 'enum',
		enum: Type,
		nullable: false
	})
	public type: Type;

	@Column({
		type: 'enum',
		enum: Provider,
		nullable: false
	})
	public provider: Provider;

	@Column('text', { nullable: false })
	public title: string;

	@Column('text', { nullable: true })
	public description?: string;

	@Column('uuid', { nullable: false })
	public siteId: string;
	@ManyToOne(() => Site, {
		eager: false,
		nullable: false,
		onDelete: 'CASCADE'
	})
	@JoinColumn({ name: 'siteId' })
	public site?: Site | Partial<Site>;

	@Column('uuid', { nullable: true })
	public businessId?: string;
	@ManyToOne(() => Business, {
		eager: false,
		nullable: true,
		onDelete: 'CASCADE'
	})
	@JoinColumn({ name: 'businessId' })
	public business?: Business | Partial<Business>;

	@Column('uuid', { nullable: true })
	public projectId?: string;
	@ManyToOne(() => Project, {
		eager: false,
		nullable: true,
		onDelete: 'CASCADE'
	})
	@JoinColumn({ name: 'projectId' })
	public project?: Project | Partial<Project>;

	@Column('uuid', { nullable: true })
	public userId?: string;
	@ManyToOne(() => User, {
		eager: false,
		nullable: true,
		onDelete: 'CASCADE'
	})
	@JoinColumn({ name: 'userId' })
	public user?: User | Partial<User>;

	@Column('text', { nullable: false })
	public assetPath: string;

	@Column('json', { nullable: true })
	public dimensions: AssetDimensions;

	@Column('text', { nullable: true })
	public mimeType: string;

	@Column('jsonb', { nullable: true, default: '{}' })
	public metadata: Record<string, any>;

	@Column('boolean', { nullable: false, default: false })
	public isRemote: boolean;

	@Index('idx_assets_local_path')
	@Column('text', { nullable: true })
	public localPath?: string;

	@Column('uuid', { array: true, nullable: true })
	public collectionIds?: string[];

	@Column('boolean', { default: false })
	public delete: boolean;

	@ManyToMany(
		() => AssetCollection,
		collection => collection.assets,
		{
			eager: false
		}
	)
	@JoinTable({
		name: 'assetCollectionItems',
		joinColumn: { name: 'assetId', referencedColumnName: 'id' },
		inverseJoinColumn: { name: 'collectionId', referencedColumnName: 'id' }
	})
	public collections?: AssetCollection[];

	@Column('timestamptz', { default: () => 'NOW()' })
	public created: string;

	@OneToMany(
		() => Business,
		business => business.logo,
		{
			onDelete: 'SET NULL'
		}
	)
	@JoinTable({ name: 'businesses' })
	public businessLogos?: Business[];

	public toPublic(exclude?: Array<keyof PublicAsset>) {
		let pub: Partial<PublicAsset> = {
			id: this.id,
			type: this.type,
			provider: this.provider,
			title: this.title,
			description: this.description,
			dimensions: this.dimensions,
			mimeType: this.mimeType,
			metadata: this.metadata,
			isRemote: this.isRemote,
			localPath: this.localPath,
			siteId: this.siteId,
			businessId: this.businessId,
			projectId: this.projectId,
			assetPath: this.assetPath,
			created: this.created,
			collectionIds: this.collectionIds,
			delete: this.delete
		};

		if (this.site && !exclude?.includes('site')) {
			pub.site = new Site(this.site).toPublic(['assets']);
		}

		if (this.business && !exclude?.includes('business')) {
			pub.business = new Business(this.business).toPublic();
		}

		if (this.project && !exclude?.includes('project')) {
			pub.project = new Project(this.project).toPublic(['assets']);
		}

		if (this.user && !exclude?.includes('user')) {
			pub.user = new User(this.user).toPublic();
		}

		if (this.collections && !exclude?.includes('collections')) {
			pub.collections = this.collections.map(collection => new AssetCollection(collection));
		}

		return pub as PublicAsset;
	}
}
